/**
 * Build a POST /profiles/export request body from the wizard's state. Pure:
 * this is the single place that knows the payload shape (kept 1:1 with the
 * original useProfileExport hook).
 */
import type { CompanyExportField, ExportDataType, ProfileExportField } from '@shared/schemas';

import type { FilterGroup } from '@/types/filters';

import type { ExportFormat, ResolvedDataType } from './export-plan.model';

/**
 * Export the DEACTIVATED list instead of the active one, narrowed the way the
 * Deactivated Profiles toolbar narrows it (#536).
 *
 * Its own block rather than three more rules in the advanced filter group: that
 * group is the client-facing search vocabulary, and deactivation is an admin
 * concern with no business in a licence user's filter builder. Its PRESENCE is
 * the scope; a separate flag would be a second field free to disagree with it.
 */
export interface DeactivatedExportScope {
  q?: string;
  reason?: string;
  keyword?: string;
  /** A profile type the profile carries (#702). */
  type?: string;
  /** ISO instants; `to` exclusive. Resolved from the page's date preset. */
  from?: string;
  to?: string;
}

/** The POST /profiles/export body (snake_case, sent as-is). */
export interface ExportRequest {
  filters: FilterGroup;
  format: ExportFormat;
  list_id?: string;
  columns?: ProfileExportField[];
  company_columns?: CompanyExportField[];
  data_type: ResolvedDataType;
  deactivated?: DeactivatedExportScope;
  /** The licence to hold the export to (PI-117), honoured for an admin only. */
  license_id?: string;
}

export interface BuildExportRequestInput {
  filters: FilterGroup;
  format: ExportFormat;
  /** List export: the list to scope on. Global export: null. */
  listId: string | null;
  /** The user's data-type selection (drives which column lists are sent). */
  dataTypes: ExportDataType[];
  profileColumns: ProfileExportField[];
  companyColumns: CompanyExportField[];
  /** `data_type` of THIS request; CSV + both splits into two requests. */
  dataType: ResolvedDataType;
  /** Set only by the Deactivated Profiles page; absent everywhere else. */
  deactivated?: DeactivatedExportScope | null;
  /**
   * Set only by the HNWIs page (PI-117): the user's licence, so an admin's file
   * holds what the table showed rather than every licence.
   */
  licenceId?: string | null;
}

export function buildExportRequest({
  filters,
  format,
  listId,
  dataTypes,
  profileColumns,
  companyColumns,
  dataType,
  deactivated,
  licenceId,
}: BuildExportRequestInput): ExportRequest {
  const wantProfiles = dataTypes.includes('profiles');
  const wantCompanies = dataTypes.includes('companies');
  return {
    filters,
    format,
    // The two scopes are mutually exclusive server-side, and `list_id` is what
    // a deactivated export never has: it covers the whole database.
    ...(listId && !deactivated ? { list_id: listId } : {}),
    ...(deactivated ? { deactivated } : {}),
    // A list or the deactivated population already fixes the scope.
    ...(licenceId && !listId && !deactivated ? { license_id: licenceId } : {}),
    ...(wantProfiles && profileColumns.length ? { columns: profileColumns } : {}),
    ...(wantCompanies && companyColumns.length ? { company_columns: companyColumns } : {}),
    data_type: dataType,
  };
}
